import { useMemo, useState } from "react";
import { FieldValues, UseFormReturn } from "react-hook-form";
import styled from "styled-components";
import { z } from "zod";
import { Button } from "../../atoms/Button";
import { Input } from "../../atoms/Input";
import FormTemplate from "../../templates/FormTemplate";
import { useButtonState } from "../../../hooks/useButtonState";

type Props = {
  email: string;
  handleSubmit: (email: string, onError: (message: string) => void) => void;
  handleCancel: () => void;
};

export const ForgotPasswordForm: React.FC<Props> = ({
  email,
  handleSubmit,
  handleCancel,
}) => {
  const [hasEmailError, setHasEmailError] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  // Use button state hook to prevent double clicks
  const { isSubmitting, executeWithLoading } = useButtonState();

  const schema = useMemo(() => {
    return z.object({
      email: z.string().min(1),
    });
  }, []);

  const [methods, setMethods] = useState<UseFormReturn<
    FieldValues,
    any,
    undefined
  > | null>(null);

  return (
    <FormTemplate
      schema={schema}
      setMethods={setMethods}
      isSubmitting={isSubmitting}
      defaultValues={{ email: "" }}
      onSubmit={({ email }) => {
        // Prevent submission if already submitting
        if (isSubmitting) {
          return;
        }

        // Use executeWithLoading to prevent double submissions
        executeWithLoading(async () => {
          // Wrap handleSubmit in a Promise to work with executeWithLoading
          return new Promise<void>((resolve, reject) => {
            handleSubmit(email, (message) => {
              setHasEmailError(true);
              setErrorMessage(message);
              reject(new Error(message));
            });

            // For successful submissions, resolve the promise
            setTimeout(resolve, 100);
          });
        }).catch((error) => {
          // Error is already handled by setting errorMessage above
          console.log('ForgotPassword form submission error handled:', error.message);
        });
      }}
    >
      <StyledTextContainer>
        Please enter your email address you’d like to reset your password.
      </StyledTextContainer>

      <Input
        label="Email"
        name="email"
        type="email"
        autoFocus
        defaultValue={email}
        hasError={hasEmailError}
        errorMessage={errorMessage}
        required
      />

      <StyledFooter>
        <Button variant="filled" text="Request reset link" type="submit" fullWidth isSubmitting={isSubmitting} />
        <Button
          variant="link"
          text="Back to login"
          type="button"
          onClick={handleCancel}
        />
      </StyledFooter>
    </FormTemplate>
  );
};

const StyledTextContainer = styled.div`
  font-size: 14px;
  font-weight: 400;
  line-height: 150%;
  margin-bottom: 24px;
  color: #697077;
`;

const StyledFooter = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 32px;
  gap: 20px;
`;
